import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import {
    Button,
    Card,
    Divider,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    shorthands,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import {
    AddRegular,
    bundleIcon,
    CheckmarkStarburstFilled,
    CheckmarkStarburstRegular,
    DocumentOnePageSparkleFilled,
    DocumentOnePageSparkleRegular,
    OpenRegular,
    TimelineFilled,
    TimelineRegular,
} from '@fluentui/react-icons'
import useEntity from '../../../../hooks/useEntity'
import { ViewFileProps } from '../ViewFile'
import Navigation, { NavigationState } from '../../../../components/Form/Navigation'
import GlobalContext, { AIAgentEnum } from '../../../../context/GlobalContext'
import { useApplicationStyles } from '../../../../pages/App'
import Loading from '../../../../components/Loading'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import SubmittingButton from '../../../components/Button/SubmittingButton'
import useAxios from '../../../../hooks/useAxios'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import { useSettings } from '../../../../hooks/useSettings'
import useDelayedInterval from '../../../../hooks/useDelayedInterval'
import { Destination } from '../../../../context/AxiosContext'
import useUser from '../../../../hooks/useUser'

export type FileAIAgentDto = {
    id: string
    created: DateTime
    fileId: string
    userId: string
    type: AIAgentEnum
    externalId: string
}

export type AIAgentResultDto<ResultT> = {
    error: object
    warning: string
    responseType: object
    data: ResultT
}

export type AIAgentProjectDto = {
    id: string
    name: string
    description: string
    legalArea: string
    legalSubArea: string
    createdAt: DateTime
    projectType: string
    isAnonymizationEnabled: boolean
}

export type AIAgentConsentDto = {
    consentUrl: string
}

export type AIAgentConsentStatusDto = {
    organisationId: string
    consentGranted: boolean
    consentGrantedAt: string
}

export type AIAgentProjectStatusDto = {
    id: string
    hasDocuments: string
    active: boolean
    anonymized: boolean
    anonymizationReviewRequired: boolean
}

export type AIAgentInteractionDto = {
    id: string
    type: AIAgentInteractionEnum
    title: string
}

export type AIAgentSummaryDto = AIAgentInteractionDto & {
    versions: object[]
    preferredVersion: object
    documents: object[]
    modifiedAt: string
    projectId: string
    userWarnings: object[]
}

export type AIAgentTimelineDto = AIAgentInteractionDto & {
    orderPreference: object
    includeDocumentIds: object[]
    events: object[]
    hasNewDocuments: boolean
    status: string
}

export enum AIAgentInteractionEnum {
    Timeline = 'TIMELINE',
    Summary = 'SUMMARY',
}

export type FileAIAgentProps = {
    agent?: AIAgentEnum
}

export type LegalPAToken = {
    userId: string
    organisationId: string
    userToken: string
    userTokenExpiresAt: string
    refreshToken: string
}

const CheckmarkStarburst = bundleIcon(CheckmarkStarburstFilled, CheckmarkStarburstRegular)
const TimelineIcon = bundleIcon(TimelineFilled, TimelineRegular)
const SummaryIcon = bundleIcon(DocumentOnePageSparkleFilled, DocumentOnePageSparkleRegular)

const useStyles = makeStyles({
    alert: {
        ...shorthands.margin(tokens.spacingHorizontalS),
    },
    container: {
        display: 'grid',
        gap: tokens.spacingHorizontalM,
    },
    containerColumn: {
        gridTemplateColumns: '150px 1fr',
    },
    containerRow: {
        gridTemplateRows: '1fr',
    },
    sidebar: {
        display: 'flex',
        minWidth: 'max-content',
        flexWrap: 'nowrap',
        gap: tokens.spacingHorizontalM,
    },
    sidebarColumn: {
        flexDirection: 'column',
    },
    sidebarRow: {
        flexDirection: 'row-reverse',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalM,
    },
    createButtonRow: {
        marginRight: tokens.spacingHorizontalL,
    },
    createButtonColumn: {
        marginLeft: tokens.spacingHorizontalM,
    },
})

export function FileAIAgentOverview(props: Partial<FileAIAgentProps> & ViewFileProps): ReactElement {
    const appStyles = useApplicationStyles()
    const axios = useAxios()
    const { host } = useContext(AuthorizationContext)
    const globalContext = useContext(GlobalContext)
    const { currentUser } = useUser()
    const { settings } = useSettings()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    /**
     * Interface state
     */
    const [selected, setSelected] = useState<NavigationState>()
    const [aiAgent] = useEntity<FileAIAgentDto>(() => ({ url: `/file/${props.file.id}/ai-agent/${props.agent}` }), props.agent || null)
    const [currentAIAgent, setCurrentAIAgent] = useState<FileAIAgentDto>()
    const [syncDisabled, setSyncDisabled] = useState<boolean>(false)

    useEffect(() => {
        if (aiAgent.loaded) {
            setCurrentAIAgent(aiAgent.data)
        }
    }, [aiAgent])

    /**
     * LegalPA state main state
     */
    const [,] = useEntity<any[]>(
        () => ({
            url: `/ai-agent/legalpa/user`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
        }),
        props.agent || null,
        result => result.data,
    )

    const [consentStatus] = useEntity<AIAgentConsentStatusDto>(
        () => ({
            url: `/ai-agent/legalpa/consent-status/${host.RowKey}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
        }),
        props.agent || null,
        result => result.data,
    )
    useEffect(() => {
        if (consentStatus.loaded && settings?.legalpa.consentComplete !== consentStatus.data.consentGranted) {
            globalContext.reset()
        }
    }, [consentStatus])

    const [currentAIToken, , resetAIToken] = useEntity<LegalPAToken>(
        () => ({
            url: `/ai-agent/legalpa/${host.RowKey}/user/token`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
        }),
        currentAIAgent?.type || null,
    )
    const { isRunning: isCurrentAITokenRunning, setRunning: setCurrentAITokenRunning } = useDelayedInterval(1_800_000, () => resetAIToken())

    //** All LegalPA API from here **/
    const [,] = useEntity<any[]>(
        () => ({
            url: `/api/v1/organizations`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken.loaded ? '1' : null,
        result => result.data,
    )
    const [,] = useEntity<any[]>(
        () => ({
            url: `/api/v1/projects`,
            baseURL: process.env.LEGALPA_API_URL,
            params: { projectTypeCategory: 'HAMMOCK' },
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken.loaded ? '1' : null,
        result => result.data,
    )

    const [consentUrl] = useEntity<AIAgentConsentDto>(
        () => ({
            url: `/api/v1/integrations/hammock/consent-url`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken.loaded ? '1' : null,
        result => result.data,
    )

    //Load AI Token when available.
    //IF no project is present AND the user presses the create button, then create a project and set it.
    useEffect(() => {
        if (!isCurrentAITokenRunning && currentAIToken.loaded) {
            setCurrentAITokenRunning(true)
        }

        if (currentAIToken.loaded && currentAIAgent?.id === undefined && currentAIAgent?.type) {
            axios
                .post<AIAgentResultDto<AIAgentProjectDto>>(
                    `/api/v1/projects`,
                    {
                        name: settings?.fileSharepointDisplayName
                            .replaceAll(/{{\s*file\.name\s*}}/g, props.file.name)
                            .replaceAll(/{{\s*file\.reference\s*}}/g, props.file.reference)
                            .replaceAll(/[/\\]/g, '-')
                            .trim(),
                        projectTypeId: '29DAE791-21CD-4097-8C9A-2802EC0A16B4',
                        hammockDossierFolderId: props.file.documentRootId,
                        isAnonymizationEnabled: true,
                    },
                    {
                        baseURL: process.env.LEGALPA_API_URL,
                        headers: { Authorization: `Bearer ${currentAIToken.data.userToken}`, SuppressErrors: 'true' },
                    },
                )
                .then(response => {
                    axios
                        .post(`/file/${props.file.id}/ai-agent`, { type: props.agent, externalId: response.data.data.id })
                        .then(response2 => setCurrentAIAgent(response2.data))
                        .then(() => resetSummaries())
                        .then(() => resetTimelines())
                })
        }
    }, [currentAIToken, currentAIAgent?.type])

    const [,] = useEntity<AIAgentProjectDto, AIAgentResultDto<AIAgentProjectDto>>(
        () => ({
            url: `/api/v1/projects/${currentAIAgent.externalId}`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken.loaded ? '1' : null,
        result => result.data?.data,
    )
    const [projectStatus, , , resetProjectStatus] = useEntity<AIAgentProjectStatusDto, AIAgentResultDto<AIAgentProjectStatusDto>>(
        () => ({
            url: `/api/v1/projects/${currentAIAgent.externalId}/status`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken.loaded ? '1' : null,
        result => result.data?.data,
    )
    const [summaries, , resetSummaries] = useEntity<AIAgentSummaryDto[], AIAgentResultDto<AIAgentSummaryDto[]>>(
        () => ({
            baseURL: process.env.LEGALPA_API_URL,
            url: `/api/v1/projects/${currentAIAgent.externalId}/summaries`,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken?.data ? currentAIAgent?.id || null : null,
        result => result.data?.data?.map(item => ({ ...item, type: AIAgentInteractionEnum.Summary })) || [],
    )
    const [timelines, , resetTimelines] = useEntity<AIAgentTimelineDto[], AIAgentResultDto<AIAgentTimelineDto>>(
        () => ({
            baseURL: process.env.LEGALPA_API_URL,
            url: `/api/v1/projects/${currentAIAgent.externalId}/timelines`,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken?.data ? currentAIAgent?.id || null : null,
        result =>
            (result.data?.data ? [result.data.data] : [])?.map(item => ({ ...item, type: AIAgentInteractionEnum.Timeline, title: 'Tijdlijn' })) || [],
    )
    const sortedInteractions: AIAgentInteractionDto[] = useMemo(() => {
        return [...(summaries.data || []), ...(timelines.data || [])]
    }, [summaries.loaded, timelines.loaded])
    const selectedInteraction: AIAgentInteractionDto = useMemo(() => {
        return sortedInteractions?.find(item => item.id === selected?.id)
    }, [sortedInteractions, selected])

    if (!props.agent) {
        return (
            <Card className={appStyles.flexRow}>
                De AI module is nog niet ingesteld. Neem contact op met <a href={'https://legalpa.ai'}>LegalPA</a> om een licentie af te sluiten en te
                koppelen met HAMMOCK.
            </Card>
        )
    }

    if (aiAgent.rejected) {
        return (
            <Card className={mergeClasses(appStyles.displayFlex, appStyles.flexAlignItemsStart)}>
                <Text>{translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.file.intro`)}</Text>
                {!settings?.legalpa?.organisationId && (
                    <Text>{translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.organisation.intro`)}</Text>
                )}
                {!currentUser?.legalPAId && (
                    <Text>{translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.account.intro`)}</Text>
                )}
                {settings.legalpa?.consentComplete !== true && (
                    <Text>{translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.consent.intro`)}</Text>
                )}
                {!currentAIAgent?.id && (
                    <SubmittingButton
                        appearance={'primary'}
                        onClick={() =>
                            Promise.resolve().then(() =>
                                setCurrentAIAgent({
                                    id: undefined,
                                    created: undefined,
                                    externalId: undefined,
                                    fileId: undefined,
                                    userId: undefined,
                                    type: AIAgentEnum.LegalPa,
                                }),
                            )
                        }
                    >
                        {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new`)}
                    </SubmittingButton>
                )}
            </Card>
        )
    }

    if (!summaries.loaded || !timelines.loaded || !sortedInteractions) {
        return <Loading />
    }

    return (
        <>
            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                <Tooltip
                    relationship="description"
                    content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.open.tooltip`)}
                    withArrow
                >
                    <Button
                        appearance="secondary"
                        icon={<OpenRegular />}
                        className={mergeClasses(styles.createButtonColumn)}
                        onClick={() => {
                            window.open(process.env.LEGALPA_APP_URL + '/dossiers/' + currentAIAgent.externalId, '_blank')
                        }}
                    >
                        {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.open`)}
                    </Button>
                </Tooltip>

                {settings.legalpa?.consentComplete === true && (
                    <Tooltip
                        relationship="description"
                        content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.synchronise.tooltip`)}
                        withArrow
                    >
                        <Button
                            appearance="secondary"
                            icon={<OpenRegular />}
                            disabled={syncDisabled}
                            onClick={() => {
                                axios
                                    .post<any, AIAgentResultDto<any>>(
                                        `/api/v1/projects/${currentAIAgent.externalId}/hammock-dossier/sync`,
                                        {},
                                        {
                                            baseURL: process.env.LEGALPA_API_URL,
                                            headers: { Authorization: `Bearer ${currentAIToken.data.userToken}`, SuppressErrors: 'true' },
                                        },
                                    )
                                    .then(() => setSyncDisabled(true))
                            }}
                        >
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.synchronise`)}
                        </Button>
                    </Tooltip>
                )}
                {settings.legalpa?.consentComplete !== true && (
                    <Tooltip
                        relationship="description"
                        content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.consent.tooltip`)}
                        withArrow
                    >
                        <Button
                            appearance="secondary"
                            icon={<OpenRegular />}
                            onClick={() => {
                                window.open(consentUrl.data.consentUrl)
                            }}
                        >
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.consent`)}
                        </Button>
                    </Tooltip>
                )}
                {projectStatus?.data?.anonymizationReviewRequired && (
                    <Tooltip
                        relationship="description"
                        content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.reviewrequired.tooltip`)}
                        withArrow
                    >
                        <Button
                            appearance="secondary"
                            icon={<OpenRegular />}
                            onClick={() => {
                                window.open(process.env.LEGALPA_APP_URL + '/dossiers/' + currentAIAgent.externalId + '/documents', '_blank')
                            }}
                        >
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.reviewrequired`)}
                        </Button>
                    </Tooltip>
                )}
            </div>
            <Divider vertical={false} />
            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                <Tooltip
                    relationship="description"
                    content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.new.tooltip`)}
                    withArrow
                >
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <MenuButton
                                appearance="primary"
                                icon={<AddRegular />}
                                className={mergeClasses(styles.createButtonColumn)}
                                onClick={() => {}}
                            >
                                {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.new`)}
                            </MenuButton>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem
                                    onClick={() => {
                                        axios
                                            .post<any, AIAgentResultDto<any>>(
                                                `/api/v1/projects/${currentAIAgent.externalId}/timelines/generate`,
                                                {},
                                                {
                                                    baseURL: process.env.LEGALPA_API_URL,
                                                    headers: { Authorization: `Bearer ${currentAIToken.data.userToken}`, SuppressErrors: 'true' },
                                                },
                                            )
                                            .then(() => {
                                                resetProjectStatus()
                                                resetTimelines()
                                            })
                                    }}
                                    disabled={!!sortedInteractions?.find(item => item.type === AIAgentInteractionEnum.Timeline)}
                                >
                                    {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.timeline`)}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        axios
                                            .post<any, AIAgentResultDto<AIAgentResultDto<string>>>(
                                                `/api/v1/projects/${currentAIAgent.externalId}/summaries/generate-stream-ws`,
                                                {},
                                                {
                                                    baseURL: process.env.LEGALPA_API_URL,
                                                    headers: { Authorization: `Bearer ${currentAIToken.data.userToken}`, SuppressErrors: 'true' },
                                                },
                                            )
                                            .then(() => {
                                                resetProjectStatus()
                                                resetSummaries()
                                            })
                                    }}
                                >
                                    {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.summary`)}
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </Tooltip>
            </div>
            <div className={mergeClasses(styles.container, styles.containerColumn, styles.containerRow)}>
                <Navigation<AIAgentInteractionDto, NavigationState>
                    entities={sortedInteractions}
                    isLoaded={summaries.loaded && timelines.loaded}
                    getEntityKey={entity => entity.id}
                    getEntityId={entity => entity.id}
                    getEntityLabel={entity => entity.title}
                    getEntityIcon={entity =>
                        entity.type === AIAgentInteractionEnum.Timeline ? (
                            <TimelineIcon />
                        ) : entity.type === AIAgentInteractionEnum.Summary ? (
                            <SummaryIcon />
                        ) : (
                            <CheckmarkStarburst />
                        )
                    }
                    selected={selected}
                    setSelected={setSelected}
                    createSelectedState={entity => ({
                        id: entity.id,
                        isNew: false,
                        isDirty: false,
                    })}
                    useColumns={true}
                />
                <div>
                    {selected?.id && (
                        <Button
                            appearance="secondary"
                            icon={<OpenRegular />}
                            onClick={() => {
                                const type = selectedInteraction.type === AIAgentInteractionEnum.Timeline ? 'timeline' : 'summary'
                                window.open(`${process.env.LEGALPA_APP_URL}/dossiers/${currentAIAgent.externalId}/${type}`, '_blank')
                            }}
                        >
                            {translate(
                                `file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.${selectedInteraction.type
                                    .toLowerCase()
                                    .replaceAll('_', '-')}.open`,
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </>
    )
}
